/**
 * Email-verification gate.
 *
 * Shown by the router in place of a protected page when the signed-in account has
 * not yet verified its email address. It is not a route: there is no URL that
 * "is" the verification screen, because the state it reflects is the account's,
 * not the address bar's.
 *
 * The gate never traps the account — it always offers a way out (sign out) and a
 * way forward (resend, then re-check). Verification state is confirmed against the
 * backend via `auth.refresh()`, never assumed from a button press.
 */

import { el, icon } from "../ui/dom.js";
import { pageHeader, errorAlert, successAlert, section } from "../ui/patterns.js";

/**
 * @param {import("../app/router.js").RouteContext} context
 * @returns {DocumentFragment}
 */
export function renderVerifyEmail(context) {
  const account = context.services.auth.getCurrentAccount();
  const frag = document.createDocumentFragment();
  frag.append(
    pageHeader(
      "Confirm your email address",
      "We have sent a verification link to your email address. Open it to confirm " +
        "your account, then come back and continue.",
    ),
  );

  const statusRegion = el("div", { "aria-live": "assertive" });

  if (account) {
    frag.append(
      el("p", { class: "kin-prose" }, [
        "We sent it to ",
        el("strong", {}, [account.email]),
        ". It can take a minute to arrive, and it may be in your spam folder.",
      ]),
    );
  }

  const continueButton = el(
    "wa-button",
    {
      variant: "brand",
      on: {
        click: async () => {
          statusRegion.replaceChildren();
          continueButton.setAttribute("loading", "");
          try {
            await context.services.auth.refresh();
            const refreshed = context.services.auth.getCurrentAccount();
            if (refreshed && refreshed.emailVerified) {
              const returnTo =
                typeof context.state.returnTo === "string" ? context.state.returnTo : "/dashboard";
              context.navigate(returnTo);
              return;
            }
            continueButton.removeAttribute("loading");
            statusRegion.replaceChildren(
              errorAlert(
                "That email address is not confirmed yet. Open the link in the email, then try again.",
              ),
            );
          } catch {
            continueButton.removeAttribute("loading");
            statusRegion.replaceChildren(
              errorAlert("We could not check your account just now. Please try again."),
            );
          }
        },
      },
    },
    [icon("arrow-right"), "I have confirmed — continue"],
  );

  const resendButton = el(
    "wa-button",
    {
      appearance: "outlined",
      on: {
        click: async () => {
          statusRegion.replaceChildren();
          resendButton.setAttribute("loading", "");
          try {
            await context.services.auth.sendVerificationEmail();
            statusRegion.replaceChildren(successAlert("We have sent another verification email."));
          } catch (error) {
            statusRegion.replaceChildren(
              errorAlert(error instanceof Error ? error.message : "Could not resend the email."),
            );
          } finally {
            resendButton.removeAttribute("loading");
          }
        },
      },
    },
    [icon("envelope"), "Resend the email"],
  );

  const signOutButton = el(
    "wa-button",
    {
      appearance: "plain",
      on: {
        click: async () => {
          await context.services.auth.signOut();
          context.navigate("/");
        },
      },
    },
    [icon("right-to-bracket"), "Sign out"],
  );

  frag.append(
    statusRegion,
    section("Next steps", [
      el("div", { class: "kin-cta-row" }, [continueButton, resendButton]),
      el("p", { class: "kin-note" }, ["Signed in on a shared device? ", signOutButton]),
    ]),
  );

  return frag;
}
